'use client';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'next/navigation';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { CourseTabList } from '@/constants/menu';
import {
  useAllCourses,
  useChapter,
  useChapterCompleted,
  useCourse,
  useRestartTest,
  useRestartUserExercise,
  useSubmitTest,
  useUpdateTimerRemaining,
  useUpdateUserExercise,
  useUserCourseStatus,
} from '@/hooks';
import { useAuth } from '@/providers';
import { useViewStore } from '@/stores';
import type { ChapterItemType, CourseContextType } from '@/types';
import { isExercise } from '@/types';
import { useRoute } from '@/utils/client/route';

const CourseContext = createContext<CourseContextType | undefined>(undefined);

export const CourseProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const { isRoute, goToRoute } = useRoute();
  const { isLoggedIn, isSessionLoaded } = useAuth();
  const { emptyShowExplanations, showMenuDrawer, toggleMenuDrawer } = useViewStore();
  const { chapterSlug, courseSlug } = useParams() as {
    courseSlug?: string;
    chapterSlug?: string;
  };

  // Data
  const [courseId, setCourseId] = useState<string | undefined>(undefined);
  const [chapterDetails, setChapterDetails] = useState<
    { id: string; type: ChapterItemType } | undefined
  >(undefined);
  const { data: courses = [], isLoading: coursesLoading } = useAllCourses();
  const { data: selectedCourse, isLoading: courseLoading } = useCourse(
    !!(courseId && isSessionLoaded),
    courseId,
  );
  const {
    data: selectedChapter,
    isLoading: chapterLoading,
    isError: chapterLoadError,
  } = useChapter(isSessionLoaded, selectedCourse?.id, chapterDetails?.id, chapterDetails?.type);
  const { data: courseStatus, isLoading: courseStatusLoading } = useUserCourseStatus(
    isLoggedIn,
    courseId,
  );

  // Virtuals
  const canRenewTest = !!(
    selectedCourse?.type === 'Test' &&
    courseStatus?.status === 'submitted' &&
    courseStatus?.repetitions &&
    courseStatus?.repetitions?.remaining > 0
  );

  const isChapterCompleted = !!(
    selectedChapter &&
    courseStatus?.chapterStatuses?.some(
      ({ chapter, completed }) => chapter === selectedChapter.id && completed === true,
    )
  );

  const isExerciseCorrect = !!(
    selectedChapter &&
    courseStatus?.chapterStatuses?.some(
      ({ chapter, questionsStatistics }) =>
        chapter === selectedChapter.id &&
        selectedChapter.type === 'Exercise' &&
        questionsStatistics?.allCorrect,
    )
  );

  const hasChapterAccess = !!(
    selectedChapter &&
    (selectedChapter.access === 'free' ||
      (selectedChapter.access === 'login' && isLoggedIn) ||
      (selectedChapter.access === 'membership' && isLoggedIn && courseStatus?.hasMembership))
  );

  // Actions
  const { mutate: mutateSubmitTest } = useSubmitTest();
  const submitTest = () => {
    if (!selectedCourse) throw new Error('No selected course');
    mutateSubmitTest(selectedCourse.id, {
      onSuccess: () => {
        if (showMenuDrawer) toggleMenuDrawer();
        goToRoute('myCourseTabs', {
          courseSlug: selectedCourse.slug,
          tabId: CourseTabList.ergebnisse,
        });
      },
    });
  };

  const { mutate: mutateRenewTest } = useRestartTest();
  const renewTest = () => {
    if (!selectedCourse) throw new Error('No selected course');
    mutateRenewTest(selectedCourse.id);
  };

  const { mutate: mutateToggleChapterCompleted } = useChapterCompleted();
  const toggleChapterCompleted = () => {
    if (!selectedCourse || !selectedChapter) {
      throw new Error('No selected course / chapter');
    }
    mutateToggleChapterCompleted({
      courseId: selectedCourse.id,
      chapterId: selectedChapter.id,
      chapterType: selectedChapter.type,
    });
  };

  const { mutate: mutateUpdateExercise } = useUpdateUserExercise();
  const updateUserExercise = (submit = false) => {
    if (!selectedCourse) throw new Error('No course');
    if (!selectedChapter || !isExercise(selectedChapter)) {
      throw new Error('Chapter is not an exercise');
    }
    mutateUpdateExercise({
      courseId: selectedCourse?.id,
      exercise: selectedChapter,
      submit,
    });
  };

  const { mutate: mutateRestartUserExercise } = useRestartUserExercise();
  const restartUserExercise = () => {
    if (!selectedCourse || !selectedChapter) {
      throw new Error('No course / chapter');
    }
    mutateRestartUserExercise({
      courseId: selectedCourse?.id,
      exerciseId: selectedChapter.id,
    });
  };

  const { mutate: mutateUpdateTimerRemaining } = useUpdateTimerRemaining();
  const updateTimerRemaining = useCallback(
    (timerRemaining: number) => {
      if (!selectedCourse) throw new Error('No course');
      mutateUpdateTimerRemaining({ courseId: selectedCourse.id, timerRemaining });
    },
    [mutateUpdateTimerRemaining, selectedCourse],
  );

  // State
  const isLoading = coursesLoading || courseLoading || chapterLoading || courseStatusLoading;

  useEffect(() => {
    if (courseSlug) {
      const id = courses.find(({ slug }) => slug === courseSlug)?.id;
      setCourseId(id);
    }
    if (chapterSlug && selectedCourse) {
      const chapterItem = selectedCourse.curriculum.find(({ item }) => item.slug === chapterSlug);
      if (chapterItem) {
        const nextDetails = {
          id: chapterItem.item.id,
          type: chapterItem.type,
        };
        setChapterDetails((prev) =>
          prev?.id === nextDetails.id && prev?.type === nextDetails.type ? prev : nextDetails,
        );
      } else {
        setChapterDetails((prev) => (prev === undefined ? prev : undefined));
      }
    }
  }, [chapterSlug, courseSlug, courses, selectedCourse]);

  useEffect(() => {
    // Remove course / chapter references if arriving in views...
    if (isRoute('myCourses')) {
      setCourseId(undefined);
      setChapterDetails(undefined);
    } else if (isRoute('myCourseTabs')) {
      setChapterDetails(undefined);
      queryClient.invalidateQueries({ queryKey: ['userCourseStatus'] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRoute, queryClient.invalidateQueries]);

  // Clear show explanations when chapter changes
  useEffect(() => {
    if (selectedChapter) {
      emptyShowExplanations();
    }
  }, [selectedChapter, emptyShowExplanations]);

  return (
    <CourseContext.Provider
      value={{
        courses,
        selectedCourse,
        selectedChapter,
        courseStatus,
        hasChapterAccess,
        isLoading,
        isChapterCompleted,
        isExerciseCorrect,
        canRenewTest,
        chapterLoadError,
        toggleChapterCompleted,
        updateUserExercise,
        restartUserExercise,
        renewTest,
        submitTest,
        updateTimerRemaining,
      }}>
      {children}
    </CourseContext.Provider>
  );
};

export function useCourses(): CourseContextType {
  const context = useContext(CourseContext);
  if (!context) {
    throw new Error('useCourses must be used within an CourseProvider');
  }
  return context;
}
